import { useCallback, useMemo, useState } from 'react';

import { useAircrafts } from './useAircrafts';

import type { AircraftData } from '#entities';

/**
 * Custom hook to manage aircraft selection state with memoization
 * Prevents unnecessary re-renders when aircraft data changes
 */
export const useAircraftSelection = () => {
  const [open, setOpen] = useState(false);
  const aircrafts = useAircrafts();

  // Memoize the first aircraft to prevent unnecessary re-renders
  const defaultAircraft = useMemo(() => aircrafts[0], [aircrafts]);

  const [selectedAircraft, setSelectedAircraft] =
    useState<AircraftData>(defaultAircraft);

  // Memoized handlers to prevent prop drilling re-renders
  const handleSelectAircraft = useCallback(
    (id: number) => {
      setOpen(true);
      const aircraft = aircrafts.find(a => a.id === id) as AircraftData;
      setSelectedAircraft(aircraft);
    },
    [aircrafts],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Return memoized values
  return useMemo(
    () => ({
      open,
      selectedAircraft,
      handleSelectAircraft,
      handleClose,
    }),
    [open, selectedAircraft, handleSelectAircraft, handleClose],
  );
};
